
import { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface Resource {
  id: string;
  resource_id: number;
  name: string;
  role: string;
  role_type?: string;
  skillset?: string;
  capacity?: string;
  rate?: number;
  availability?: string;
  years_experience?: number;
  past_projects?: string;
  email?: string;
  user_id: string;
  project_id?: string;
  project_name?: string;
  created_at: string;
  updated_at: string;
}

export interface ResourceAssignment {
  id: string;
  project_id: string;
  task_id: string;
  resource_id: string;
  start_date: string;
  end_date: string;
  allocation_hrs: string | null;
  allocated_percentage: number | null;
  utilization_percentage: number | null;
  email?: string;
  user_id: string;
}

export interface ResourceUtilization {
  date: string;
  resourceId: string;
  resourceName: string;
  hours: number;
  taskId: string;
  taskName: string;
}

export const useResourceManagement = (projectId: string | null, projectName?: string | null) => {
  const [resources, setResources] = useState<Resource[]>([]);
  const [resourceAssignments, setResourceAssignments] = useState<ResourceAssignment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [canViewResourcePool, setCanViewResourcePool] = useState<boolean>(false);
  const [canEditResource, setCanEditResource] = useState<boolean>(true);
  const [canImportResource, setCanImportResource] = useState<boolean>(true);
  const [userRole, setUserRole] = useState<string>('');

  const [isAddResourceDialogOpen, setIsAddResourceDialogOpen] = useState(false);
  const [isEditResourceDialogOpen, setIsEditResourceDialogOpen] = useState(false);
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [isAddAssignmentDialogOpen, setIsAddAssignmentDialogOpen] = useState(false);
  const [isEditAssignmentDialogOpen, setIsEditAssignmentDialogOpen] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState<ResourceAssignment | null>(null);

  useEffect(() => {
    if (projectId) {
      fetchResources();
      fetchResourceAssignments();
    }
  }, [projectId, projectName]);

  const fetchResources = async () => {
    if (!projectId) return;

    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setResources([]);
        setCanViewResourcePool(false);
        return;
      }

      // Look up caller role + company_name
      const { data: profile } = await supabase
        .from('users')
        .select('role, company_name')
        .eq('id', user.id)
        .maybeSingle();

      const role = (profile?.role || '').trim();
      const company = (profile?.company_name || '').trim();
      setUserRole(role);

      // Only Project Director or Project Manager can ever view the Resource Pool
      if (role !== 'Director' && role !== 'Manager') {
        setCanViewResourcePool(false);
        setCanEditResource(false);
        setCanImportResource(false);
        setResources([]);
        return;
      }

      const viewColumn = role === 'Director'
        ? 'rm_view_resourcepool__project_director'
        : 'rm_view_resourcepool__project_manager';
      const importColumn = role === 'Director'
        ? 'rm_import__project_director'
        : 'rm_import__project_manager';

      let allowed = true;
      let editAllowed = true;
      let importAllowed = true;
      if (company) {
        const { data: perm } = await supabase
          .from('project_access_permissions')
          .select(`${viewColumn}, rm_edit_resource__project_manager, ${importColumn}`)
          .eq('company_name', company)
          .maybeSingle();
        if (perm && (perm as any)[viewColumn] === false) allowed = false;
        if (role === 'Manager') {
          editAllowed = perm
            ? (perm as any).rm_edit_resource__project_manager !== false
            : true;
        }
        if (perm && (perm as any)[importColumn] === false) importAllowed = false;
      }

      setCanEditResource(editAllowed);
      setCanImportResource(importAllowed);


      if (!allowed) {
        setCanViewResourcePool(false);
        setResources([]);
        return;
      }

      setCanViewResourcePool(true);

      if (!projectName) {
        setResources([]);
        return;
      }

      // Use service-role edge function to fetch all resources for this project_name.
      // Both Directors and Managers go through this path so they always see the
      // same Resource Pool (resources added by their counterpart are visible).
      const { data: fnData, error: fnError } = await supabase.functions.invoke(
        'get-project-resource-pool',
        { body: { project_name: projectName } }
      );
      if (fnError) throw fnError;
      setResources((fnData?.data as Resource[]) || []);
    } catch (error) {
      console.error('Error fetching resources:', error);
      toast.error('Failed to load resources');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchResourceAssignments = async () => {
    if (!projectId) return;
    
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Determine role to choose the correct data path. Directors and Managers
      // use the service-role edge function so the assignment list is
      // synchronized between them, regardless of who created each row.
      const { data: profile } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();
      const role = (profile?.role || '').trim();

      if (role === 'Manager' || role === 'Director') {
        const { data: fnData, error } = await supabase.functions.invoke(
          'get-manager-resource-assignments',
          { body: { project_id: projectId } }
        );
        if (error) throw error;
        setResourceAssignments((fnData?.data as ResourceAssignment[]) || []);
      } else {
        const { data, error } = await supabase
          .from('resource_assignment')
          .select('*')
          .eq('project_id', projectId)
          .eq('user_id', user.id);
        if (error) throw error;
        setResourceAssignments(data || []);
      }
    } catch (error) {
      console.error('Error fetching resource assignments:', error);
      toast.error('Failed to load resource assignments');
    } finally {
      setIsLoading(false);
    }
  };

  const addResource = async (resourceData: any) => {
    if (!projectId) {
      alert('No project selected. Please select a project first.');
      return false;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('You must be logged in to add resources.');
        return false;
      }

      // Compute next resource_id across the full project pool so Directors and
      // Managers don't clash. Use current resources list (already fetched via
      // service-role edge function for both roles).
      let nextResourceId = 1;
      if (resources.length > 0) {
        const ids = resources
          .map(r => r.resource_id)
          .filter(id => id != null && !isNaN(id as any));
        if (ids.length > 0) nextResourceId = Math.max(...ids) + 1;
      }

      const newResourceData = {
        ...resourceData,
        resource_id: nextResourceId,
        project_id: projectId,
        project_name: projectName ?? resourceData.project_name,
      };

      console.log("Adding resource with data:", newResourceData);

      if (userRole === 'Manager' || userRole === 'Director') {
        const { data, error } = await supabase.functions.invoke(
          'manage-project-resource',
          { body: { action: 'insert', payload: newResourceData } }
        );
        if (error || (data as any)?.error) {
          const msg = (data as any)?.error || error?.message || 'Unknown error';
          alert(`Failed to add resource: ${msg}`);
          return false;
        }
      } else {
        const { error } = await supabase
          .from('resources')
          .insert({ ...newResourceData, user_id: user.id });
        if (error) {
          console.error("Error inserting resource:", error);
          alert(`Failed to add resource: ${error.message}`);
          return false;
        }
      }

      toast.success('Resource added successfully');
      setIsAddResourceDialogOpen(false);
      fetchResources();
      return true;
    } catch (error) {
      console.error('Error adding resource:', error);
      alert('An unexpected error occurred while adding the resource. Please try again.');
      return false;
    }
  };

  const updateResource = async (resourceId: string, resourceData: any) => {
    try {
      if (userRole === 'Manager' || userRole === 'Director') {
        const { data, error } = await supabase.functions.invoke(
          'manage-project-resource',
          { body: { action: 'update', id: resourceId, payload: resourceData } }
        );
        if (error || (data as any)?.error) {
          const msg = (data as any)?.error || error?.message || 'Unknown error';
          alert(`Failed to update resource: ${msg}`);
          return false;
        }
      } else {
        const { error } = await supabase
          .from('resources')
          .update(resourceData)
          .eq('id', resourceId);
        if (error) {
          console.error("Error updating resource:", error);
          alert(`Failed to update resource: ${error.message}`);
          return false;
        }
      }

      toast.success('Resource updated successfully');
      setIsEditResourceDialogOpen(false);
      setSelectedResource(null);
      fetchResources();
      return true;
    } catch (error) {
      console.error('Error updating resource:', error);
      alert('An unexpected error occurred while updating the resource. Please try again.');
      return false;
    }
  };

  const deleteResource = async (resourceId: string) => {
    try {
      if (userRole === 'Manager' || userRole === 'Director') {
        const { data, error } = await supabase.functions.invoke(
          'manage-project-resource',
          { body: { action: 'delete', id: resourceId } }
        );
        if (error || (data as any)?.error) {
          const msg = (data as any)?.error || error?.message || 'Unknown error';
          alert(`Failed to delete resource: ${msg}`);
          return false;
        }
      } else {
        const { error } = await supabase
          .from('resources')
          .delete()
          .eq('id', resourceId);
        if (error) {
          console.error("Error deleting resource:", error);
          alert(`Failed to delete resource: ${error.message}`);
          return false;
        }
      }

      toast.success('Resource deleted successfully');
      setSelectedResource(null);
      fetchResources();
      return true;
    } catch (error) {
      console.error('Error deleting resource:', error);
      alert('An unexpected error occurred while deleting the resource. Please try again.');
      return false;
    }
  };

  const addResourceAssignment = async (assignmentData: any) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        alert('You must be logged in to add resource assignments.');
        return false;
      }

       // Fetch the associated resource to get the email
       const { data: resourceData, error: resourceError } = await supabase
       .from('resources')
       .select('email')
       .eq('id', assignmentData.resource_id)
       .single();

     if (resourceError) {
       console.error('Error fetching resource email:', resourceError);
       alert('Failed to fetch resource email. Please try again.');
       return false;
     }

      const { error } = await supabase
        .from('resource_assignment')
        .insert({ ...assignmentData, user_id: user.id, email: resourceData?.email });

      if (error) {
        console.error("Error adding resource assignment:", error);
        alert(`Failed to add resource assignment: ${error.message}`);
        return false;
      }

      toast.success('Resource assignment added successfully');
      setIsAddAssignmentDialogOpen(false);
      fetchResourceAssignments();
      return true;
    } catch (error) {
      console.error('Error adding resource assignment:', error);
      alert('An unexpected error occurred while adding the resource assignment. Please try again.');
      return false;
    }
  };

  const updateResourceAssignment = async (assignmentId: string, assignmentData: any) => {
    try {
       // Fetch the associated resource to get the email
       const { data: resourceData, error: resourceError } = await supabase
       .from('resources')
       .select('email')
       .eq('id', assignmentData.resource_id)
       .single();

     if (resourceError) {
       console.error('Error fetching resource email:', resourceError);
       alert('Failed to fetch resource email. Please try again.');
       return false;
     }

      const { error } = await supabase
        .from('resource_assignment')
        .update({ ...assignmentData, email: resourceData?.email })
        .eq('id', assignmentId);

      if (error) {
        console.error("Error updating resource assignment:", error);
        alert(`Failed to update resource assignment: ${error.message}`);
        return false;
      }

      toast.success('Resource assignment updated successfully');
      setIsEditAssignmentDialogOpen(false);
      setSelectedAssignment(null);
      fetchResourceAssignments();
      return true;
    } catch (error) {
      console.error('Error updating resource assignment:', error);
      alert('An unexpected error occurred while updating the resource assignment. Please try again.');
      return false;
    }
  };

  const deleteResourceAssignment = async (assignmentId: string) => {
    try {
      const { error } = await supabase
        .from('resource_assignment')
        .delete()
        .eq('id', assignmentId);

      if (error) {
        console.error("Error deleting resource assignment:", error);
        alert(`Failed to delete resource assignment: ${error.message}`);
        return false;
      }

      toast.success('Resource assignment deleted successfully');
      setSelectedAssignment(null);
      fetchResourceAssignments();
      return true;
    } catch (error) {
      console.error('Error deleting resource assignment:', error);
      alert('An unexpected error occurred while deleting the resource assignment. Please try again.');
      return false;
    }
  };

  return {
    resources,
    resourceAssignments,
    isLoading,
    isAddResourceDialogOpen,
    setIsAddResourceDialogOpen,
    isEditResourceDialogOpen,
    setIsEditResourceDialogOpen,
    selectedResource,
    setSelectedResource,
    isAddAssignmentDialogOpen,
    setIsAddAssignmentDialogOpen,
    isEditAssignmentDialogOpen,
    setIsEditAssignmentDialogOpen,
    selectedAssignment,
    setSelectedAssignment,
    addResource,
    updateResource,
    deleteResource,
    addResourceAssignment,
    updateResourceAssignment,
    deleteResourceAssignment,
    refreshResources: fetchResources,
    canViewResourcePool,
    canEditResource,
    canImportResource,
    userRole,
  };
};

